/**
 * La cortina que se pone entre un proyecto y otro.
 *
 * Cambiar de proyecto cambia TODO lo que hay debajo —el menú, las secciones,
 * las cifras— y hasta ahora ese cambio ocurría de golpe: la pantalla se quedaba
 * igual de forma y distinta de contenido, y había que leerla entera para saber
 * si el clic había hecho algo. Un panel que se rellena solo con otros números no
 * dice a dónde fuiste; sólo dice que algo se movió.
 *
 * Aquí se dice en voz alta, y con una sola imagen: el nombre del que dejas sube
 * y se va, y el que llega entra desde abajo a ocupar su sitio, recortados los
 * dos por el renglón como el rodillo de un contador. El relevo tiene dirección,
 * que es lo que convierte «algo cambió» en «vas de A a B».
 *
 * Y de paso cubre el hueco real: al otro lado hay peticiones que tardan. Sin
 * cortina esa espera se veía como esqueletos grises donde antes había datos.
 */
import { useEffect, useRef, useState } from 'react'
import { LoadingMascot } from '@webbuilder/ui'
import { roleLabelKey, type ProjectAccessRole } from '@webbuilder/shared-types'
import type { Translate } from '@webbuilder/i18n'
import { useI18n, translate } from '../lib/i18n'
import { roleIcon } from '../lib/roleIcons'

/**
 * Lo que tarda la mascota en posarse derecha cuando se le quita el giro.
 *
 * No es un número de aquí: es `LAND_MS` de `LoadingMascot`, que es quien frena.
 * Se repite para poder restarlo, y si allí cambia, aquí también.
 */
const MASCOT_BRAKE_MS = 520

/**
 * Lo que dura el relevo entero: el nombre viejo yéndose más el nuevo llegando.
 *
 * Se exporta porque la cortina de ELIMINAR encadena este mismo relevo al final
 * de su cuenta atrás, y necesita saber cuándo termina para retirarse. Dos
 * copias del número serían dos guiones que se desincronizan en cuanto uno se
 * ajuste.
 *
 * Los dos van en serie y no cruzados, así que esto es la SUMA de las dos
 * animaciones de `.dash-project-switch-name` en la hoja de estilos. Si allí se
 * estira una, este número sube con ella.
 */
export const RELAY_MS = 680 + 820

/** Lo que tarda la cortina en ponerse y en quitarse. Casa con la hoja de estilos. */
export const FADE_MS = 420

/**
 * Lo que cada nombre se queda quieto y a la vista: el mismo número los dos.
 *
 * Antes no lo era —el de salida tenía casi medio segundo y el de llegada dos
 * décimas— y se notaba como que el proyecto nuevo se enseñaba de refilón: te
 * daban tiempo de leer de dónde salías y no de leer a dónde habías llegado,
 * que es justo el dato al revés. Siendo el mismo, los dos nombres pesan igual y
 * el relevo queda centrado entre dos pausas iguales.
 */
export const HOLD_MS = 420

/**
 * Lo que el giro se adelanta al relevo por delante y se retrasa por detrás.
 *
 * Arrancando y parando exactamente con las letras, el giro se encendía y se
 * apagaba como un interruptor pegado a ellas. Empezando antes ya está en marcha
 * cuando el nombre se mueve —el movimiento no nace, se encuentra ya hecho— y
 * acabando después no deja los dos finales en el mismo fotograma.
 */
const SPIN_LEAD_MS = 260

/** Cuándo arranca el relevo: con la cortina ya opaca y el nombre leído. */
const SWAP_AT = FADE_MS + HOLD_MS

/**
 * El guion, en milisegundos desde que la cortina se pone.
 *
 * Escrito como cuentas y no como cifras sueltas: son cinco momentos encadenados
 * —el giro arranca, el relevo empieza, la mascota frena para llegar parada,
 * la cortina se va, la cortina se quita— y con los números a pelo cualquier
 * ajuste a uno dejaba a los otros apuntando a donde ya no había nada.
 *
 * `swap` es el único que no es decoración: ahí es donde el panel de abajo cambia
 * de proyecto de verdad, y es también donde arranca el relevo. Va con la
 * pantalla en su momento más ocupado —el renglón cambiando de nombre— así que
 * cualquier salto de maquetación del panel al recomponerse ocurre detrás de la
 * cortina y con la vista puesta en otra cosa.
 */
const SCRIPT = {
  spin: SWAP_AT - SPIN_LEAD_MS,
  swap: SWAP_AT,
  brake: SWAP_AT + RELAY_MS + SPIN_LEAD_MS - MASCOT_BRAKE_MS,
  leave: SWAP_AT + RELAY_MS + HOLD_MS,
  end: SWAP_AT + RELAY_MS + HOLD_MS + FADE_MS,
} as const

/**
 * El mismo guion para quien pidió que no se mueva nada.
 *
 * No se salta la cortina —tapar el cambio sigue siendo útil, y sin ella el panel
 * se recompondría a la vista— pero deja de ser una escena: aparece, cambia y se
 * va en un cuarto de segundo. Las animaciones de dentro las apaga la hoja de
 * estilos con la misma consulta.
 */
const SCRIPT_STILL = { spin: 0, swap: 40, brake: 40, leave: 120, end: 260 } as const

/** Un proyecto sin nombre sigue teniendo que ocupar el renglón. */
function label(name: string): string {
  return name.trim() || translate('common.unnamed')
}

/**
 * Qué eres en el proyecto al que llegas, en una línea.
 *
 * Se dice AQUÍ porque el relevo es el único momento en que las dos respuestas se
 * comparan: sales de uno tuyo y entras en uno ajeno, o al revés, y el cambio de
 * lo que puedes hacer es tan grande como el cambio de nombre. Sabiéndolo al
 * llegar, el menú recortado que hay debajo ya no sorprende.
 *
 * «(invitado)» y no un rol a secas: sin esa palabra, «Editor» se lee como una
 * descripción del sitio —que es de edición— y no como una descripción de ti.
 *
 * La etiqueta sale del catálogo, sin una segunda forma en masculino: «Diseño»
 * aquí y «Diseño» en Equipo y en la pastilla del nombre son la misma cosa dicha
 * en tres sitios, y una variante suelta obligaría a mantener dos listas.
 */
function roleLine(role: ProjectAccessRole | undefined, t: Translate): string | null {
  if (!role) return null
  return role === 'owner'
    ? t('role.ownerLine')
    : t('role.guest', { role: t(roleLabelKey(role)) })
}

function ProjectRoleLine({ role, className = '', hidden = false }: {
  role: ProjectAccessRole
  className?: string
  hidden?: boolean
}) {
  const { t } = useI18n()
  const Icon = roleIcon(role)
  return (
    <p className={`dash-project-switch-role${className}`} aria-hidden={hidden || undefined}>
      <Icon size={13} stroke={2.2} aria-hidden="true" />
      {roleLine(role, t)}
    </p>
  )
}

export function ProjectSwitchOverlay({ fromName, toName, fromRole, toRole, onSwap, onDone }: {
  /** De dónde vienes. Puede llegar vacío si el nombre aún no se sabía. */
  fromName: string
  toName: string
  /** Qué eras allí y qué eres aquí. Ausentes mientras no se sepan. */
  fromRole?: ProjectAccessRole | undefined
  toRole?: ProjectAccessRole | undefined
  /** Cambiar el panel de abajo. Se llama UNA vez, a mitad de la cortina. */
  onSwap: () => void
  /** Retirar la cortina. Quien la puso es quien la quita. */
  onDone: () => void
}) {
  const [arrived, setArrived] = useState(false)
  const [leaving, setLeaving] = useState(false)
  /**
   * Si la mascota está girando ahora mismo.
   *
   * Sólo lo está mientras las letras se mueven. Antes giraba de principio a fin
   * —era su estado por defecto— y eso la convertía en un indicador de carga
   * puesto ahí para siempre: seguía dando vueltas con el nombre nuevo ya quieto
   * en su sitio, o sea, anunciando una espera que ya había terminado. Girando
   * sólo durante el relevo, el giro dice lo que está pasando en vez de rellenar
   * el rato.
   */
  const [spinning, setSpinning] = useState(false)

  /* Los avisos en una referencia y el efecto sin dependencias: son funciones
     nuevas en cada render de quien nos monta, y con ellas en la lista el guion
     se reiniciaba desde el principio cada vez que el panel de abajo repintaba
     —que es constantemente, porque justo entonces está cargando el proyecto
     nuevo—. La cortina no llegaba nunca a su final. */
  const swapRef = useRef(onSwap)
  const doneRef = useRef(onDone)
  swapRef.current = onSwap
  doneRef.current = onDone

  useEffect(() => {
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const script = still ? SCRIPT_STILL : SCRIPT
    const timers = [
      window.setTimeout(() => { setArrived(true); swapRef.current() }, script.swap),
      window.setTimeout(() => setLeaving(true), script.leave),
      window.setTimeout(() => doneRef.current(), script.end),
    ]
    /* Sin movimiento no hay giro en absoluto: arrancarlo y frenarlo con las dos
       esperas casi pegadas dejaría una vuelta suelta de un fotograma, que es
       justo el tirón que se pidió no ver. */
    if (!still) {
      timers.push(
        window.setTimeout(() => setSpinning(true), script.spin),
        /* El freno se pide ANTES del final que se busca, porque pararse le cuesta
           media vuelta larga: pedirlo en el instante en que se quiere quieta la
           dejaría girando media pantalla después. Lo que se calcula es dónde
           tiene que ACABAR —un pelo pasado el relevo— y de ahí se resta su
           frenada. */
        window.setTimeout(() => setSpinning(false), script.brake),
      )
    }
    return () => { for (const timer of timers) window.clearTimeout(timer) }
  }, [])

  return (
    <div className={`dash-project-switch${leaving ? ' is-leaving' : ''}`} role="presentation">
      {/* Sólo el destino se anuncia: leer «sales de A» y a las pocas décimas
          «entras en B» convierte un cambio en dos noticias. */}
      <p className="dash-sr-only" role="status" aria-live="polite">
        {arrived ? translate('switch.arrived', { name: label(toName) }) : translate('switch.switching')}
      </p>

      <div className="dash-project-switch-stage">
        {/* La mascota de las esperas, la de sólo contorno: es la misma que sale
            al arrancar la aplicación y al cargar una lista, y ésta es esa misma
            clase de momento. La sólida que saluda no vale aquí — mira al ratón,
            reacciona al pasar por encima y salta al pulsarla, y una cortina que
            responde a los gestos invita a jugar con ella justo cuando lo único
            que se quiere es que se quite.

            El envoltorio existe para poder moverla: dentro, el giro lo lleva el
            propio componente sobre el contorno, así que animar el marco de fuera
            no le pisa nada.

            `restWhenIdle` es lo que le permite quedarse quieta en vez de irse:
            sin él, dejar de girar se entiende como «ya llegaron los datos» y la
            mascota se desvanece. Aquí no se despide —la cortina entera se
            encarga de eso— sólo se para y se posa derecha. */}
        <span className={`dash-project-switch-mascot${arrived ? ' is-relay' : ''}`}>
          <LoadingMascot spinning={spinning} size={76} restWhenIdle />
        </span>

        <div className="dash-project-switch-plate">
          {/* Los dos a la vez y en la misma celda: el relevo es que se releven.
              Antes el viejo se desmontaba en el mismo fotograma en que nacía el
              nuevo —no se iba, desaparecía— y lo que se veía era un nombre
              apareciendo, sin rastro de dónde venías.

              El que sale es decorativo para quien lee con lector de pantalla: ya
              lo tenía en pantalla hasta hace un instante, y anunciarlo otra vez
              al irse es contar el cambio dos veces. */}
          {arrived ? (
            <span className="dash-project-switch-name is-out" aria-hidden="true">{label(fromName)}</span>
          ) : null}
          <span className={`dash-project-switch-name${arrived ? ' is-in' : ''}`}>
            {label(arrived ? toName : fromName)}
          </span>
        </div>

        {/* El rol tiene su propia placa sin recorte. Al llegar se conservan las
            dos líneas durante el relevo: si se sustituyera una por otra, React
            desmontaría la primera antes de que pudiera hacer su fade out. */}
        {fromRole || toRole ? (
          <div className="dash-project-switch-role-plate">
            {arrived && fromRole ? (
              <ProjectRoleLine role={fromRole} className=" is-out" hidden />
            ) : null}
            {!arrived && fromRole ? <ProjectRoleLine role={fromRole} /> : null}
            {arrived && toRole ? <ProjectRoleLine role={toRole} className=" is-in" /> : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
